import { useEffect } from "react";
import { skipToken, useQuery } from "@apollo/client/react";
import queryString from "query-string";
import {
  constructEvergreenTaskLogURL,
  getEvergreenTestLogURL,
} from "@evg-ui/lib/constants/logURLTemplates";
import { reportError } from "@evg-ui/lib/utils/errorReporting";
import { LogRenderingTypes, LogTypes } from "constants/enums";
import { getEvergreenJobLogsURL } from "constants/externalURLTemplates";
import {
  getEvergreenCompleteLogsURL,
  getEvergreenTaskFileURL,
  getEvergreenTaskLogURL,
} from "constants/logURLTemplates";
import {
  TaskFilesQuery,
  TaskFilesQueryVariables,
  TestLogUrlAndRenderingTypeQuery,
  TestLogUrlAndRenderingTypeQueryVariables,
} from "gql/generated/types";
import { GET_TEST_LOG_URL_AND_RENDERING_TYPE, TASK_FILES } from "gql/queries";
import { useTaskQuery } from "hooks/useTaskQuery";

interface UseResolveLogURLAndRenderingTypeProps {
  execution?: string;
  fileName?: string;
  // This groupID comes from the application URL.
  groupID?: string;
  excludeTimestamps?: boolean;
  logType: string;
  origin?: string;
  taskID?: string;
  testID?: string;
}

type HookResult = {
  /** The URL of the file parsley should download */
  downloadURL: string;
  /** The URL of the log file in the html log viewer */
  htmlLogURL: string;
  /** The URL of the RESMOKE logs job logs page in Spruce */
  jobLogsURL: string;
  /** The URL of the log file without any processing */
  rawLogURL: string;
  /** Whether the hook is actively making an network request or not  */
  loading: boolean;
  /** The rendering logic to use for the log when available */
  renderingType: LogRenderingTypes;
  /** The failing command in the log, if available */
  failingCommand: string;
  /** Evergreen test log path (for Evergreen CLI --log_path) */
  logPath: string;
  /** Logs to merge alongside this test log (for Evergreen CLI --logs_to_merge) */
  logsToMerge: string[];
};

/**
 * `useResolveLogURL` is a custom hook that resolves the log URL based on the log type and other parameters.
 * @param UseResolveLogURLAndRenderingTypeProps - The props for the hook
 * @param UseResolveLogURLAndRenderingTypeProps.execution - The execution number of the log
 * @param UseResolveLogURLAndRenderingTypeProps.fileName - The name of the file being viewed
 * @param UseResolveLogURLAndRenderingTypeProps.groupID - The group ID of the test given from the URL
 * @param UseResolveLogURLAndRenderingTypeProps.excludeTimestamps - Whether to exclude timestamps from the log URLs
 * @param UseResolveLogURLAndRenderingTypeProps.logType - The type of log being viewed
 * @param UseResolveLogURLAndRenderingTypeProps.origin - The origin of the log
 * @param UseResolveLogURLAndRenderingTypeProps.taskID - The task ID of the log
 * @param UseResolveLogURLAndRenderingTypeProps.testID - The test ID of the log
 * @returns LogURLs
 */
export const useResolveLogURLAndRenderingType = ({
  excludeTimestamps = false,
  execution,
  fileName,
  groupID,
  logType,
  origin,
  taskID,
  testID,
}: UseResolveLogURLAndRenderingTypeProps): HookResult => {
  const { loading: isLoadingTask, task } = useTaskQuery({
    execution,
    taskID,
  });

  // Testlog LogRenderingType is queried from EVG, the rest are inferred from logType.
  const { data: testData, loading: isLoadingTest } = useQuery<
    TestLogUrlAndRenderingTypeQuery,
    TestLogUrlAndRenderingTypeQueryVariables
  >(
    GET_TEST_LOG_URL_AND_RENDERING_TYPE,
    logType === LogTypes.EVERGREEN_TEST_LOGS && taskID && execution && testID
      ? {
          variables: {
            execution: parseInt(execution as string, 10),
            taskID: taskID as string,
            testName: `^${testID}$`,
          },
        }
      : skipToken,
  );

  const { data: taskFileData, loading: isLoadingTaskFileData } = useQuery<
    TaskFilesQuery,
    TaskFilesQueryVariables
  >(
    TASK_FILES,
    logType === LogTypes.EVERGREEN_TASK_FILE && taskID && execution
      ? {
          variables: {
            execution: parseInt(execution as string, 10),
            taskId: taskID as string,
          },
        }
      : skipToken,
  );

  let downloadURL = "";
  let rawLogURL = "";
  let htmlLogURL = "";
  let jobLogsURL = "";
  let renderingType: LogRenderingTypes = LogRenderingTypes.Default;
  let failingCommand = "";
  let logPath = "";
  let logsToMerge: string[] = [];
  switch (logType) {
    case LogTypes.EVERGREEN_COMPLETE_LOGS: {
      if (!taskID || !execution || !groupID) {
        break;
      }
      downloadURL = getEvergreenCompleteLogsURL(taskID, execution, groupID);
      rawLogURL = downloadURL;
      jobLogsURL = getEvergreenJobLogsURL(taskID, execution, groupID);
      renderingType = LogRenderingTypes.Resmoke;
      break;
    }
    case LogTypes.EVERGREEN_TASK_FILE: {
      if (!taskID || !execution || isLoadingTaskFileData) {
        break;
      }
      if (taskID && execution && fileName) {
        downloadURL = getEvergreenTaskFileURL(
          taskID,
          execution,
          encodeURIComponent(fileName),
        );
        const allFiles = taskFileData?.task?.files.groupedFiles.flatMap(
          (group) => group.files,
        );
        rawLogURL =
          allFiles?.find((file) => file?.name === fileName)?.link || "";
      }
      renderingType = LogRenderingTypes.Default;
      break;
    }
    case LogTypes.EVERGREEN_TASK_LOGS: {
      if (!taskID || !origin || !execution || isLoadingTask) {
        break;
      }
      const timeParam = !excludeTimestamps;
      downloadURL = task?.logs
        ? getEvergreenTaskLogURL(task.logs, origin, {
            priority: true,
            text: true,
            time: timeParam,
          })
        : constructEvergreenTaskLogURL(taskID, execution, origin, {
            priority: true,
            text: true,
            time: timeParam,
          });
      rawLogURL = task?.logs
        ? getEvergreenTaskLogURL(task.logs, origin, {
            text: true,
            time: timeParam,
          })
        : constructEvergreenTaskLogURL(taskID, execution, origin, {
            text: true,
            time: timeParam,
          });
      htmlLogURL = task?.logs
        ? getEvergreenTaskLogURL(task.logs, origin, {
            text: false,
            time: timeParam,
          })
        : constructEvergreenTaskLogURL(taskID, execution, origin, {
            text: false,
            time: timeParam,
          });
      renderingType = LogRenderingTypes.Default;

      // TODO DEVPROD-9689: Parsley should not examine TaskEndDetail.description GQL type to determine failing log line
      const potentialFailingCommand =
        task?.details?.failingCommand || task?.details?.description;
      failingCommand =
        task?.details?.status === "failed" && potentialFailingCommand
          ? potentialFailingCommand
          : "";
      break;
    }
    case LogTypes.EVERGREEN_TEST_LOGS: {
      if (!taskID || !execution || !testID || isLoadingTest || !testData) {
        break;
      }
      const { groupID: groupIDFromQuery, logs } =
        testData?.task?.tests.testResults[0] || {};
      const {
        logPath: logPathFromQuery,
        logsToMerge: logsToMergeFromQuery,
        renderingType: renderingTypeFromQuery,
        url,
        urlRaw,
      } = logs || {};

      const isResmoke = renderingTypeFromQuery === LogRenderingTypes.Resmoke;
      const printTime = isResmoke ? false : !excludeTimestamps;
      const baseRawLogURL =
        urlRaw ??
        getEvergreenTestLogURL(taskID, execution, testID, { text: true });
      const baseHtmlLogURL =
        url ??
        getEvergreenTestLogURL(taskID, execution, testID, { text: false });
      rawLogURL = queryString.stringifyUrl({
        query: { print_time: printTime },
        url: baseRawLogURL,
      });
      htmlLogURL = queryString.stringifyUrl({
        query: { print_time: printTime },
        url: baseHtmlLogURL,
      });
      downloadURL = rawLogURL;
      logPath = logPathFromQuery ?? "";
      logsToMerge = logsToMergeFromQuery ?? [];
      if (!renderingTypeFromQuery) {
        renderingType = LogRenderingTypes.Default;
      } else if (
        Object.values<string>(LogRenderingTypes).includes(
          renderingTypeFromQuery,
        )
      ) {
        renderingType = renderingTypeFromQuery as LogRenderingTypes;
      } else {
        renderingType = LogRenderingTypes.Default;
      }
      if (groupIDFromQuery) {
        jobLogsURL = getEvergreenJobLogsURL(
          taskID,
          execution,
          groupIDFromQuery,
        );
      }
      break;
    }
    default:
      break;
  }

  // Report error when an unsupported renderingType is encountered.
  useEffect(() => {
    const { logs } = testData?.task?.tests.testResults[0] || {};
    const { renderingType: renderingTypeFromQuery, urlRaw } = logs || {};

    if (
      logType === LogTypes.EVERGREEN_TEST_LOGS &&
      renderingTypeFromQuery &&
      !Object.values(LogRenderingTypes).includes(
        renderingTypeFromQuery as LogRenderingTypes,
      )
    ) {
      reportError(new Error("Encountered unsupported renderingType"), {
        context: {
          rawLogURL: urlRaw || rawLogURL,
          unsupportedRenderingType: renderingTypeFromQuery,
        },
      }).severe();
    }
  }, [testData, logType, rawLogURL]);

  return {
    downloadURL,
    failingCommand,
    htmlLogURL,
    jobLogsURL,
    loading: isLoadingTest || isLoadingTask || isLoadingTaskFileData,
    logPath,
    logsToMerge,
    rawLogURL,
    renderingType,
  };
};
